import styles from "./main.module.scss";
import { ReactComponent as Logo } from "../../assets/James-Hope-University-Logo.svg";
import Stepper from "../../components/stepper/stepper";
import {
  Institution,
  PersonalInfo,
  Qualification,
  RequiredDocument,
  ReferenceFormStep,
  Review,
  UtmeResult,
  WorkHistory,
} from "../../components/onboardingSteps";
import { useAtom, useAtomValue } from "jotai";
import { pageIndexAtom, userAtom } from "../../utils/store";
import { useQuery } from "@tanstack/react-query";
import { getAllDisplayTab } from "../../utils/request";
import { logout } from "../../utils/logout";
import { useApplicationPeriod } from "../../hooks/useApplicationPeriod";
import { useGetApplicantDetailsQuery } from "../../hooks/api/useGetApplicantDetailsQuery";
import { useGetRequiredDocumentsQuery } from "../../hooks/api/useGetRequiredDocumentsQuery";
import { useGetStudentUploadedDocumentsQuery } from "../../hooks/api/useGetStudentUploadedDocumentsQuery";
import ProgramInfo from "../../components/programInfo";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { routes } from "../../routes";

type TabKey =
  | "personal-info"
  | "qualification"
  | "required-documents"
  | "utme"
  | "work-history"
  | "institution"
  | "reference-form"
  | "review";

const normalizeTabName = (tabName: string) =>
  tabName.trim().toLowerCase().replace(/\s+/g, " ");

const getTabKey = (tabName: string): TabKey | null => {
  const normalizedTabName = normalizeTabName(tabName);

  if (
    normalizedTabName === "personal information" ||
    normalizedTabName === "bio-data" ||
    normalizedTabName === "bio data" ||
    normalizedTabName === "biodata"
  ) {
    return "personal-info";
  }

  if (
    normalizedTabName === "qualification" ||
    normalizedTabName === "qualifications"
  ) {
    return "qualification";
  }

  if (normalizedTabName.includes("document")) {
    return "required-documents";
  }

  if (
    normalizedTabName === "utme" ||
    normalizedTabName === "utme score" ||
    normalizedTabName === "utme result" ||
    normalizedTabName === "utme results"
  ) {
    return "utme";
  }

  if (
    normalizedTabName === "work experience" ||
    normalizedTabName === "work history"
  ) {
    return "work-history";
  }

  if (
    normalizedTabName === "institution" ||
    normalizedTabName === "institutions"
  ) {
    return "institution";
  }

  if (
    normalizedTabName === "reference form" ||
    normalizedTabName === "reference" ||
    normalizedTabName === "references" ||
    normalizedTabName === "referee form"
  ) {
    return "reference-form";
  }

  if (normalizedTabName === "review") {
    return "review";
  }

  return null;
};

const isBachelorApplicant = (programType?: string) =>
  programType?.trim().toLowerCase().startsWith("b") ?? false;

const getNormalizedModeOfEntry = (modeOfEntry?: string) =>
  modeOfEntry?.trim().toLowerCase().replace(/\s+/g, "");

const shouldIncludeTab = (
  tab: DisplayTab,
  programType?: string,
  modeOfEntry?: string,
) => {
  if (!isBachelorApplicant(programType)) return true;

  const tabKey = getTabKey(tab.tabName);
  const normalizedModeOfEntry = getNormalizedModeOfEntry(modeOfEntry);

  if (normalizedModeOfEntry?.includes("directentry")) {
    if (tabKey === "utme") return false;
    if (tabKey === "institution") return true;
  }

  if (normalizedModeOfEntry?.includes("utme")) {
    if (tabKey === "institution") return false;
    if (tabKey === "utme") return true;
  }

  return true;
};

const hasSavedUtmeResult = () => {
  try {
    const savedUtmeResult = JSON.parse(
      localStorage.getItem("utme-result") ?? "null",
    );

    return !!(
      savedUtmeResult?.examNumber &&
      savedUtmeResult?.examYear &&
      savedUtmeResult?.jambResultMeta &&
      savedUtmeResult?.subjects?.length
    );
  } catch (_error) {
    return false;
  }
};

// Completion is derived from the backend (required document types for this
// program vs. what this applicant has actually uploaded), not from a
// localStorage flag. A flat localStorage key isn't scoped per applicant, so
// a different applicant on the same browser would incorrectly inherit a
// previous applicant's "completed" state and get auto-skipped past this tab.
const hasCompletedRequiredDocuments = (
  requiredDocuments: RequiredDocument[] | undefined,
  uploadedDocuments: StudentUploadedDocument[] | undefined,
  isDocumentsDataLoading: boolean,
) => {
  if (isDocumentsDataLoading) return false;

  const mandatoryDocuments = (requiredDocuments ?? []).filter(
    (doc) => doc.isRequired,
  );

  if (!mandatoryDocuments.length) return true;

  const uploadedDocumentIds = new Set(
    (uploadedDocuments ?? []).map((doc) => doc.requiredDocumentId),
  );

  return mandatoryDocuments.every((doc) => uploadedDocumentIds.has(doc.id));
};

const hasCompletedReferenceStep = () =>
  localStorage.getItem("reference-form-completed") === "true";

const tabComponents: Record<TabKey, JSX.Element> = {
  "personal-info": <PersonalInfo onClose={() => {}} />,
  qualification: <Qualification />,
  "required-documents": <RequiredDocument />,
  utme: <UtmeResult />,
  "work-history": <WorkHistory />,
  institution: <Institution />,
  "reference-form": <ReferenceFormStep />,
  review: <Review />,
};

const ONBOARDING_FORCE_START_KEY = "force-onboarding-start";

const Onboarding = () => {
  const [pageIndex, setPageIndex] = useAtom(pageIndexAtom);
  const storedUser = useAtomValue(userAtom);
  const { applicantDetails } = useGetApplicantDetailsQuery({
    applicantId: storedUser?.applicantId,
  });
  const user = applicantDetails ?? storedUser;
  const { isApplicationPeriodActive } = useApplicationPeriod();
  const navigate = useNavigate();
  const shouldForceFirstStepRef = useRef(
    sessionStorage.getItem(ONBOARDING_FORCE_START_KEY) === "true",
  );
  const didForceFirstStepRef = useRef(shouldForceFirstStepRef.current);
  // Guards the auto-navigate-to-first-incomplete-tab effect below so it only
  // ever repositions the applicant once per visit. Without this, a background
  // refetch (of applicant details/required documents/etc.) after the
  // applicant has manually clicked "Previous" or a step number would recompute
  // findFirstIncompleteTab and snap pageIndex back forward, making backward
  // navigation appear broken.
  const hasAutoNavigatedRef = useRef(false);

  const tabParams = {
    sessionId: user?.sessionId ?? 0,
    batchName: user?.applicationName ?? "",
    programType: user?.programTypeId ?? 0,
  };

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["get-display-tab"],
    queryFn: () => getAllDisplayTab(tabParams),
  });

  const {
    documents: requiredDocuments,
    isLoading: isRequiredDocumentsLoading,
  } = useGetRequiredDocumentsQuery({
    programTypeId: user?.programTypeId,
  });
  const {
    documents: uploadedRequiredDocuments,
    isLoading: isUploadedRequiredDocumentsLoading,
  } = useGetStudentUploadedDocumentsQuery({
    applicantId: user?.applicantId,
  });

  const sortedTabs = useMemo(
    () =>
      data?.data
        ?.slice()
        .sort((a, b) => a.tabNumber - b.tabNumber)
        .filter((tab) =>
          shouldIncludeTab(tab, user?.programType, user?.modeOfEntry),
        ) ?? [],
    [data?.data, user?.modeOfEntry, user?.programType],
  );

  useEffect(() => {
    if (user?.isPaymentSubmitted) {
      navigate(routes.dashboard.dashboard);
    }
  }, [navigate, user?.isPaymentSubmitted]);

  const findFirstIncompleteTab = useCallback(() => {
    if (!sortedTabs.length) return 1;

    for (let index = 0; index < sortedTabs.length; index += 1) {
      const tab = sortedTabs[index];
      const tabKey = getTabKey(tab.tabName);
      const stepPosition = index + 1;

      if (tabKey === "personal-info" && user?.isPersonalInfoSubmitted !== true)
        return stepPosition;

      if (tabKey === "qualification" && user?.isqualificationSubmitted !== true)
        return stepPosition;

      if (
        tabKey === "required-documents" &&
        !hasCompletedRequiredDocuments(
          requiredDocuments,
          uploadedRequiredDocuments,
          isRequiredDocumentsLoading || isUploadedRequiredDocumentsLoading,
        )
      ) {
        return stepPosition;
      }

      if (tabKey === "utme" && !hasSavedUtmeResult()) {
        return stepPosition;
      }

      if (tabKey === "work-history" && user?.isworkhistorySubmitted !== true) {
        return stepPosition;
      }

      if (tabKey === "institution" && user?.iInstitutionSubmitted !== true) {
        return stepPosition;
      }

      if (
        tabKey === "reference-form" &&
        user?.isReferenceSubmitted !== true &&
        !hasCompletedReferenceStep()
      ) {
        return stepPosition;
      }
    }

    return sortedTabs.length;
  }, [
    sortedTabs,
    user,
    requiredDocuments,
    uploadedRequiredDocuments,
    isRequiredDocumentsLoading,
    isUploadedRequiredDocumentsLoading,
  ]);

  useEffect(() => {
    if (!sortedTabs.length) return;
    if (hasAutoNavigatedRef.current) return;

    if (shouldForceFirstStepRef.current) {
      shouldForceFirstStepRef.current = false;
      sessionStorage.removeItem(ONBOARDING_FORCE_START_KEY);
      hasAutoNavigatedRef.current = true;
      setPageIndex(1);
      return;
    }

    if (didForceFirstStepRef.current) return;

    // Wait for the data findFirstIncompleteTab depends on before locking in
    // the auto-navigated position, otherwise a still-loading required
    // documents query could land the applicant on the wrong tab permanently.
    if (isRequiredDocumentsLoading || isUploadedRequiredDocumentsLoading)
      return;

    const firstIncompleteTab = findFirstIncompleteTab();
    hasAutoNavigatedRef.current = true;
    setPageIndex((currentPageIndex) =>
      currentPageIndex !== firstIncompleteTab
        ? firstIncompleteTab
        : currentPageIndex,
    );
  }, [
    findFirstIncompleteTab,
    setPageIndex,
    sortedTabs,
    isRequiredDocumentsLoading,
    isUploadedRequiredDocumentsLoading,
  ]);

  const handleStepClick = (step: number) => {
    if (step <= pageIndex) {
      setPageIndex(step);
    }
  };

  const steps = () => {
    if (isLoading) return <p>Loading...</p>;
    if (isError) return <p>Error: {error.message}</p>;

    const currentTab = sortedTabs[pageIndex - 1];
    const currentTabKey = currentTab ? getTabKey(currentTab.tabName) : null;

    if (currentTabKey) {
      return tabComponents[currentTabKey];
    }

    return <Review />;
  };

  const displaySteps = sortedTabs.map((item) => item?.tabName);

  const isActiveApplicationPeriod = isApplicationPeriodActive();

  if (!isActiveApplicationPeriod) {
    return (
      <main className={styles.onboarding}>
        <nav className={styles.nav}>
          <Logo />
        </nav>
        <button onClick={logout} className={styles.logout}>
          Logout
        </button>
        <hr className={styles.divider} />
        <br />

        <div className={styles.card}>
          <h2>Application Period Inactive</h2>
          <p>
            The application period is currently inactive. Please check back
            later.
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className={styles.onboarding}>
      <nav className={styles.nav}>
        <Logo />
      </nav>
      <button onClick={logout} className={styles.logout}>
        Logout
      </button>
      <hr className={styles.divider} />
      <br />
      <ProgramInfo className={styles.programInfo} />
      <br />
      <Stepper
        titles={displaySteps}
        noOfSteps={displaySteps?.length}
        currentStep={Number(pageIndex)}
        onStepClick={handleStepClick}
      />
      <br />

      <div className={styles.card}>
        {steps()}
        <br />
      </div>
    </main>
  );
};

export default Onboarding;
